'use client'

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { Bell, X } from 'lucide-react'
import { registerForPush } from '@/lib/fcm'
import { toast } from 'sonner'

const DISMISS_KEY = 'sasha_notify_dismissed'

export function NotificationPrompt() {
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    // Notification API support is all we need for the local check-in/check-out
    // reminders; FCM push is a best-effort bonus handled inside enable().
    if (typeof Notification === 'undefined') return
    if (Notification.permission === 'granted') return
    if (Notification.permission === 'denied') return
    if (localStorage.getItem(DISMISS_KEY)) return
    setShow(true)
  }, [])

  if (!show) return null

  const enable = async () => {
    setBusy(true)
    try {
      // Grant permission first — this alone is enough for the local check-in /
      // check-out reminders (they fire via the Notification API, no server push).
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        // Best-effort: also register for server push (task / leave updates).
        // Missing VAPID config or messaging support must not block reminders.
        registerForPush().catch(() => {})
        toast.success('Notifications enabled')
        setShow(false)
      } else {
        toast.error('Notification permission denied')
      }
    } finally {
      setBusy(false)
    }
  }

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setShow(false)
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50">
      <GlassCard tone="orange" inset className="p-4 flex items-start gap-3">
        <div className="shrink-0 rounded-lg bg-orange-500/15 ring-1 ring-inset ring-orange-500/30 p-2">
          <Bell className="h-4 w-4 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-900 dark:text-neutral-900 dark:text-white/90 font-medium">Enable Notifications</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-600 dark:text-white/60 mt-1">Get reminders for check-in/out and updates on tasks &amp; leave requests.</p>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={enable}
              disabled={busy}
              className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-medium shadow-lg shadow-orange-500/30 border-0"
            >
              {busy ? 'Enabling…' : 'Enable'}
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} className="text-neutral-700 dark:text-neutral-700 dark:text-white/70 hover:text-neutral-900 dark:hover:text-neutral-900 dark:text-white/90 hover:bg-neutral-100 dark:hover:bg-white/[0.06]">
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-neutral-400 dark:text-neutral-400 dark:text-white/40 hover:text-neutral-700 dark:hover:text-neutral-800 dark:text-white/80 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </GlassCard>
    </div>
  )
}
